import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { resolveRouteSeo, SITE_URL, SITE_OG_IMAGE } from "@/seo/meta";
import { applyJsonLd, removeJsonLd } from "@/seo/jsonLd";

function upsertMeta(attr: "name" | "property", key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = resolveRouteSeo(pathname);

    if (!seo) {
      // Unknown / non-indexable route (e.g. 404): drop route-specific schema.
      removeJsonLd();
      return;
    }

    const url = `${SITE_URL}${pathname}`;
    const image = seo.image || SITE_OG_IMAGE;

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertCanonical(url);

    upsertMeta("property", "og:type", seo.ogType || "website");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", image);

    applyJsonLd(pathname, pathname);
  }, [pathname]);

  return null;
}